<template>
  <div class="grid home-page-wrapper">
    <Loader v-if="loader" />

    <template v-if="isAssistenzaClienti">
      <div class="card col-12 welcome-card welcome-card--compact">
        <h2 class="welcome-title welcome-title--compact">Benvenuto</h2>
        <span class="email">{{ currentUser.user[0].email }}</span>
      </div>

      <div class="col-12 assistenza-home">
        <div class="grid">
          <div class="col-12 md:col-6">
            <section class="assistenza-panel assistenza-panel--ticket">
              <header class="assistenza-panel-header">
                <div class="assistenza-panel-header-left">
                  <i class="pi pi-ticket"></i>
                  <span>Ticket</span>
                  <span v-if="ticketsPanel && !ticketListLoading" class="chiamate-count-badge">{{ ticketTotal }}</span>
                </div>
                <button
                  type="button"
                  class="chiamate-refresh-btn"
                  title="Aggiorna"
                  :disabled="ticketListLoading"
                  @click="refreshTickets"
                >
                  <i :class="ticketListLoading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'"></i>
                </button>
              </header>
              <div class="assistenza-panel-body assistenza-panel-body--ticket">
                <Tickets ref="ticketsPanel" list-in-dialog />
              </div>
            </section>
          </div>

          <div class="col-12 md:col-6">
            <section class="assistenza-panel assistenza-panel--chiamate">
              <header class="assistenza-panel-header">
                <div class="assistenza-panel-header-left">
                  <i class="pi pi-phone"></i>
                  <span>Chiamate</span>
                  <span v-if="!loadingChiamate" class="chiamate-count-badge">{{ chiamatePagination.total }}</span>
                </div>
                <button
                  type="button"
                  class="chiamate-refresh-btn"
                  title="Aggiorna"
                  :disabled="loadingChiamate"
                  @click="refreshContattiDaChiamare"
                >
                  <i :class="loadingChiamate ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'"></i>
                </button>
              </header>

              <div class="assistenza-panel-body assistenza-panel-body--chiamate">
                <div class="chiamate-toolbar">
                  <label class="chiamate-days-label">Ultimi</label>
                  <select v-model.number="chiamateDays" class="chiamate-days-select" @change="onChiamateDaysChange">
                    <option :value="1">1 giorno</option>
                    <option :value="3">3 giorni</option>
                    <option :value="7">7 giorni</option>
                    <option :value="15">15 giorni</option>
                  </select>
                </div>

                <div v-if="loadingChiamate" class="chiamate-state">
                  <i class="pi pi-spin pi-spinner"></i>
                  <span>Caricamento contatti...</span>
                </div>

                <div v-else-if="chiamateError" class="chiamate-state chiamate-state--error">
                  <i class="pi pi-exclamation-triangle"></i>
                  <span>{{ chiamateError }}</span>
                  <button type="button" class="chiamate-retry-btn" @click="refreshContattiDaChiamare">Riprova</button>
                </div>

                <div v-else-if="contattiDaChiamare.length === 0" class="chiamate-state">
                  <i class="pi pi-check-circle"></i>
                  <span>Nessun contatto da richiamare</span>
                </div>

                <template v-else>
                  <div class="chiamate-list">
                    <div
                      v-for="contatto in contattiDaChiamare"
                      :key="contatto.last_missed_id || contatto.from_number"
                      class="chiamate-item"
                    >
                      <div class="chiamate-item-main">
                        <div class="chiamate-item-top">
                          <button
                            type="button"
                            class="chiamate-number"
                            @click="openCallDialog(contatto)"
                          >
                            {{ formatPhone(contatto.from_number) }}
                          </button>
                          <span class="chiamate-chip">
                            {{ contatto.missed_count }} {{ contatto.missed_count === 1 ? 'mancata' : 'mancate' }}
                          </span>
                        </div>

                        <div class="chiamate-name" :class="{ 'chiamate-name--unknown': !getContattoNome(contatto) }">
                          {{ getContattoNome(contatto) || 'Cliente non riconosciuto' }}
                        </div>

                        <div class="chiamate-meta">
                          <span v-if="contatto.code_cliente" class="chiamate-meta-item">
                            Cod. {{ contatto.code_cliente }}
                          </span>
                          <span v-if="contatto.codfis" class="chiamate-meta-item">
                            CF {{ contatto.codfis }}
                          </span>
                          <span class="chiamate-date">
                            Ultima: {{ formatDateTime(contatto.last_missed_at) }}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        class="chiamate-incall-btn"
                        title="Chiama"
                        :disabled="callingInProgress"
                        @click="openCallDialog(contatto)"
                      >
                        <i class="pi pi-phone"></i>
                      </button>
                    </div>
                  </div>

                  <div class="chiamate-pagination">
                    <span class="chiamate-pagination-info">
                      {{ chiamatePagination.from }}-{{ chiamatePagination.to }} di {{ chiamatePagination.total }}
                    </span>
                    <div class="chiamate-pagination-actions">
                      <button
                        type="button"
                        class="chiamate-page-btn"
                        :disabled="loadingChiamate || chiamatePagination.page <= 1"
                        @click="goToChiamatePage(chiamatePagination.page - 1)"
                      >
                        <i class="pi pi-chevron-left"></i>
                      </button>
                      <span class="chiamate-page-label">
                        {{ chiamatePagination.page }} / {{ chiamatePagination.last_page }}
                      </span>
                      <button
                        type="button"
                        class="chiamate-page-btn"
                        :disabled="loadingChiamate || !chiamatePagination.has_more"
                        @click="goToChiamatePage(chiamatePagination.page + 1)"
                      >
                        <i class="pi pi-chevron-right"></i>
                      </button>
                    </div>
                  </div>
                </template>
              </div>
            </section>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="card col-12 welcome-card">
        <h2 class="welcome-title">Benvenuto</h2>
        <span class="email">{{ currentUser.user[0].email }}</span>
        <a
          class="mt-5 flex align-items-center justify-content-center w-full wizard-banner"
          href="https://wizard.askmanagement.it/validazione-caricamenti-validatori"
          v-if="currentUser.user[0] && currentUser.user[0].profile && (currentUser.user[0].profile.id === 2 || currentUser.user[0].profile.id === 11)"
        >
          <div class="text-center text-white">Vai a Valida contratti</div>
        </a>
      </div>

      <div class="col-12 info-button-container" v-if="!currentUser.user[0].validate_agenzie">
        <router-link to="/info-ciclo">
          <Button label="Info Ciclo" class="info-btn" icon="pi pi-info-circle" iconPos="right" />
        </router-link>
      </div>
    </template>

    <div v-if="showCallDialog" class="chiamate-modal-overlay" @click="closeCallDialog">
      <div class="chiamate-modal" @click.stop>
        <div class="chiamate-modal-header">
          <h3>Conferma Chiamata</h3>
          <button type="button" class="chiamate-modal-close" @click="closeCallDialog">&times;</button>
        </div>
        <div class="chiamate-modal-body">
          <p>
            Vuoi chiamare il numero
            <strong>{{ selectedCallContatto?.from_number }}</strong>
            <template v-if="getContattoNome(selectedCallContatto)">
              ({{ getContattoNome(selectedCallContatto) }})
            </template>?
          </p>
        </div>
        <div class="chiamate-modal-footer">
          <button type="button" class="chiamate-btn-secondary" :disabled="callingInProgress" @click="closeCallDialog">
            No
          </button>
          <button type="button" class="chiamate-btn-primary" :disabled="callingInProgress" @click="confirmCall">
            <i v-if="callingInProgress" class="pi pi-spin pi-spinner"></i>
            <span>{{ callingInProgress ? 'Chiamata in corso...' : 'Sì, chiama' }}</span>
          </button>
        </div>
      </div>
    </div>

    <div v-if="showCallNotesDialog" class="chiamate-modal-overlay" @click="closeCallNotesDialog">
      <div class="chiamate-modal" @click.stop>
        <div class="chiamate-modal-header">
          <h3>Appunti chiamata</h3>
          <button type="button" class="chiamate-modal-close" @click="closeCallNotesDialog">&times;</button>
        </div>
        <div class="chiamate-modal-body">
          <p class="chiamate-notes-hint">
            Chiamata verso <strong>{{ selectedCallContatto?.from_number }}</strong>
          </p>
          <textarea
            v-model="callNotes"
            class="chiamate-notes-textarea"
            rows="5"
            placeholder="Inserisci gli appunti della chiamata..."
          ></textarea>
        </div>
        <div class="chiamate-modal-footer">
          <button type="button" class="chiamate-btn-secondary" :disabled="savingCallNotes" @click="closeCallNotesDialog">
            Chiudi
          </button>
          <button
            type="button"
            class="chiamate-btn-primary"
            :disabled="savingCallNotes || !callNotes.trim() || !currentCallId"
            @click="saveCallNotes"
          >
            <i v-if="savingCallNotes" class="pi pi-spin pi-spinner"></i>
            <span>{{ savingCallNotes ? 'Salvataggio...' : 'Salva appunti' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from 'vuex'
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast'
import Loader from '@/components/Loader.vue'
import Tickets from '@/components/tickets/Tickets.vue'
import IncallService from '@/services/ask-management/Incall-service'

export default {
  components: {
    Loader,
    Tickets,
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const toast = useToast()
    const currentUser = ref({ user: [{ email: '', validate_agenzie: '' }] })
    const loader = ref(false);
    const contattiDaChiamare = ref([])
    const loadingChiamate = ref(false)
    const chiamateError = ref('')
    const chiamateDays = ref(3)
    const chiamatePage = ref(1)
    const chiamateLimit = ref(20)
    const chiamatePagination = ref({
      page: 1,
      limit: 20,
      total: 0,
      last_page: 1,
      from: 0,
      to: 0,
      has_more: false,
    })
    const showCallDialog = ref(false)
    const showCallNotesDialog = ref(false)
    const selectedCallContatto = ref(null)
    const callingInProgress = ref(false)
    const currentCallId = ref(null)
    const callNotes = ref('')
    const savingCallNotes = ref(false)
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus
    self.$http = self.appContext.config.globalProperties.$http
    const incallService = new IncallService(self.$http, self.$eventBus)

    const isAssistenzaClienti = computed(() => {
      const profile = currentUser.value?.user?.[0]?.profile
      if (!profile) return false
      return profile.assistenza_clienti === 1 && profile.all !== 1
    })

    const ticketsPanel = ref(null)
    const ticketTotal = computed(() => ticketsPanel.value?.unreadTicketsTotal ?? 0)
    const ticketListLoading = computed(() => !!ticketsPanel.value?.listLoading)

    const refreshTickets = () => {
      ticketsPanel.value?.getTickets?.(true)
    }

    const formatPhone = (value) => {
      if (!value) return '-'
      return String(value)
    }

    const getContattoNome = (contatto) => {
      if (!contatto) return ''
      return String(contatto.nome || contatto.ragsoc || '').trim()
    }

    const formatDateTime = (value) => {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const getCurrentUserId = () => {
      try {
        const user = currentUser.value
        if (user?.user && Array.isArray(user.user) && user.user.length > 0) {
          return user.user[0].id || user.user[0].cod_user || user.user[0].user_id || null
        }
        return user?.id || user?.cod_user || user?.user_id || null
      } catch (error) {
        return null
      }
    }

    const openCallDialog = (contatto) => {
      if (!contatto?.from_number) {
        toast.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: 'Numero di telefono non disponibile',
          life: 3000,
        })
        return
      }
      selectedCallContatto.value = contatto
      showCallDialog.value = true
    }

    const closeCallDialog = () => {
      if (callingInProgress.value) return
      showCallDialog.value = false
    }

    const confirmCall = async () => {
      const number = selectedCallContatto.value?.from_number
      const askId = getCurrentUserId()

      if (!number) {
        toast.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: 'Numero di telefono non disponibile',
          life: 3000,
        })
        return
      }

      if (!askId) {
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile recuperare l\'ID utente corrente',
          life: 3000,
        })
        return
      }

      callingInProgress.value = true
      try {
        const result = await incallService.makeCall(askId, number, '')
        currentCallId.value = result?.chiamata_id || null
        callNotes.value = ''

        toast.add({
          severity: result?.success ? 'success' : 'info',
          summary: result?.success ? 'Chiamata avviata' : 'Esito chiamata',
          detail: result?.message || (result?.success ? 'Chiamata avviata con successo' : 'Richiesta di chiamata inviata'),
          life: 3000,
        })

        showCallDialog.value = false
        showCallNotesDialog.value = true
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.response?.data?.message || error?.message || 'Errore durante l\'avvio della chiamata',
          life: 4000,
        })
        showCallDialog.value = false
      } finally {
        callingInProgress.value = false
      }
    }

    const closeCallNotesDialog = () => {
      if (savingCallNotes.value) return
      showCallNotesDialog.value = false
      callNotes.value = ''
    }

    const saveCallNotes = async () => {
      if (!currentCallId.value || !callNotes.value.trim()) {
        return
      }

      savingCallNotes.value = true
      try {
        await incallService.saveCallNotes(currentCallId.value, callNotes.value)
        toast.add({
          severity: 'success',
          summary: 'Appunti salvati',
          detail: 'Appunti della chiamata salvati correttamente',
          life: 3000,
        })
        showCallNotesDialog.value = false
        callNotes.value = ''
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.response?.data?.message || error?.message || 'Errore nel salvataggio degli appunti',
          life: 4000,
        })
      } finally {
        savingCallNotes.value = false
      }
    }

    const loadContattiDaChiamare = async (page = chiamatePage.value) => {
      loadingChiamate.value = true
      chiamateError.value = ''
      try {
        const res = await incallService.getContactToCall(
          chiamateDays.value,
          page,
          chiamateLimit.value
        )
        const payload = res?.items || {}
        if (payload.success === false) {
          throw new Error(payload.message || 'Errore nel recupero dei contatti')
        }
        contattiDaChiamare.value = Array.isArray(payload.data) ? payload.data : []
        const pagination = payload.pagination || {}
        chiamatePage.value = pagination.page || page
        chiamatePagination.value = {
          page: pagination.page || page,
          limit: pagination.limit || chiamateLimit.value,
          total: pagination.total || 0,
          last_page: pagination.last_page || 1,
          from: pagination.from || 0,
          to: pagination.to || 0,
          has_more: !!pagination.has_more,
        }
      } catch (error) {
        contattiDaChiamare.value = []
        chiamatePagination.value = {
          page: 1,
          limit: chiamateLimit.value,
          total: 0,
          last_page: 1,
          from: 0,
          to: 0,
          has_more: false,
        }
        chiamateError.value = error?.response?.data?.message
          || error?.message
          || 'Errore nel recupero dei contatti da richiamare'
      } finally {
        loadingChiamate.value = false
      }
    }

    const refreshContattiDaChiamare = () => {
      loadContattiDaChiamare(chiamatePage.value)
    }

    const onChiamateDaysChange = () => {
      chiamatePage.value = 1
      loadContattiDaChiamare(1)
    }

    const goToChiamatePage = (page) => {
      const nextPage = Number(page)
      if (!Number.isInteger(nextPage) || nextPage < 1) return
      if (nextPage > chiamatePagination.value.last_page) return
      if (nextPage === chiamatePagination.value.page) return
      loadContattiDaChiamare(nextPage)
    }

    onMounted(() => {
      currentUser.value = JSON.parse(atob(store.state.auth.user))

      const addMobileClass = () => {
        if (window.innerWidth <= 768) {
          document.body.classList.add('home-page-mobile');
        } else {
          document.body.classList.remove('home-page-mobile');
        }
      };

      addMobileClass();

      window.addEventListener('resize', addMobileClass);

      self.$eventBus.emit('page-change', {
        target: self,
        currentRoute: router.currentRoute.value
      });

      if (isAssistenzaClienti.value) {
        loadContattiDaChiamare()
      }

      return () => {
        document.body.classList.remove('home-page-mobile');
        window.removeEventListener('resize', addMobileClass);
      };
    })

    return {
      currentUser,
      loader,
      isAssistenzaClienti,
      ticketsPanel,
      ticketTotal,
      ticketListLoading,
      refreshTickets,
      contattiDaChiamare,
      loadingChiamate,
      chiamateError,
      chiamateDays,
      chiamatePagination,
      loadContattiDaChiamare,
      refreshContattiDaChiamare,
      onChiamateDaysChange,
      goToChiamatePage,
      formatPhone,
      formatDateTime,
      getContattoNome,
      showCallDialog,
      showCallNotesDialog,
      selectedCallContatto,
      callingInProgress,
      currentCallId,
      callNotes,
      savingCallNotes,
      openCallDialog,
      closeCallDialog,
      confirmCall,
      closeCallNotesDialog,
      saveCallNotes,
    }
  }
}
</script>

<style scoped>
.welcome-card {
  padding: 4rem 2rem;
  color: white;
  background-color: var(--blue-800);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  border-radius: 10px;
  margin-bottom: 1.5rem;
}

.welcome-card--compact {
  padding: 2rem 1.5rem;
  margin-bottom: 1rem;
}

.welcome-title {
  font-size: 3.5rem;
  margin-bottom: 1rem;
  text-align: center;
  width: 100%;
}

.welcome-title--compact {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.email {
  font-size: 1.2rem;
  text-align: center;
  word-break: break-word;
  width: 100%;
}

.info-button-container {
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 0 1rem;
}

.info-btn {
  background-color: #f5a742;
  border-color: #f5a742;
  color: white;
  font-weight: bold;
  min-width: 150px;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
}

.assistenza-home {
  width: 100%;
}

.assistenza-panel {
  background: #fff;
  border: 1px solid #dbe3ec;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
  min-height: 280px;
}

.assistenza-panel--ticket,
.assistenza-panel--chiamate {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 200px);
  min-height: 780px;
  max-height: none;
  margin-bottom: 0;
}

.assistenza-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.85rem 1.1rem;
  background: linear-gradient(135deg, #0d3c61, #1a5490);
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

.assistenza-panel-header-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.assistenza-panel-body {
  padding: 1rem;
}

.assistenza-panel-body--ticket {
  padding: 0.5rem;
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.assistenza-panel-body--chiamate {
  padding: 0.75rem;
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
  max-height: none;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow: hidden;
}

.chiamate-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  font-size: 0.75rem;
  font-weight: 700;
}

.chiamate-refresh-btn {
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.chiamate-refresh-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.chiamate-toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.chiamate-days-label {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 600;
}

.chiamate-days-select {
  border: 1px solid #dbe3ec;
  border-radius: 8px;
  padding: 0.35rem 0.55rem;
  font-size: 0.85rem;
  color: #0f172a;
  background: #fff;
}

.chiamate-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  color: #64748b;
  text-align: center;
  min-height: 240px;
}

.chiamate-state .pi {
  font-size: 1.5rem;
}

.chiamate-state--error {
  color: #b91c1c;
}

.chiamate-retry-btn {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 0.35rem 0.75rem;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
}

.chiamate-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding-right: 0.2rem;
}

.chiamate-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-shrink: 0;
  padding-top: 0.35rem;
  border-top: 1px solid #e2e8f0;
}

.chiamate-pagination-info {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 600;
}

.chiamate-pagination-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.chiamate-page-btn {
  width: 1.9rem;
  height: 1.9rem;
  border: 1px solid #dbe3ec;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.chiamate-page-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.chiamate-page-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #0f172a;
  min-width: 3.5rem;
  text-align: center;
}

.chiamate-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.8rem 0.9rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  flex-shrink: 0;
}

.chiamate-item-main {
  min-width: 0;
  flex: 1;
}

.chiamate-item-top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.25rem;
}

.chiamate-number {
  display: inline-block;
  font-weight: 700;
  color: #0f172a;
  text-decoration: none;
  font-variant-numeric: tabular-nums;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
}

.chiamate-number:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.chiamate-name {
  font-size: 0.92rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.3rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chiamate-name--unknown {
  color: #94a3b8;
  font-weight: 500;
  font-style: italic;
}

.chiamate-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}

.chiamate-meta-item {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  background: #e2e8f0;
  color: #334155;
  font-size: 0.72rem;
  font-weight: 600;
}

.chiamate-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 0.75rem;
  font-weight: 700;
}

.chiamate-date {
  font-size: 0.8rem;
  color: #64748b;
}

.chiamate-incall-btn {
  flex-shrink: 0;
  width: 2.1rem;
  height: 2.1rem;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e8f1ff;
  color: #1d4ed8;
  text-decoration: none;
  border: none;
  cursor: pointer;
}

.chiamate-incall-btn:hover:not(:disabled) {
  background: #dbeafe;
}

.chiamate-incall-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.chiamate-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 1rem;
}

.chiamate-modal {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.2);
  overflow: hidden;
}

.chiamate-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.chiamate-modal-header h3 {
  margin: 0;
  font-size: 1.05rem;
  color: #0f172a;
}

.chiamate-modal-close {
  border: none;
  background: transparent;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
}

.chiamate-modal-body {
  padding: 1rem;
  color: #334155;
}

.chiamate-notes-hint {
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
}

.chiamate-notes-textarea {
  width: 100%;
  border: 1px solid #dbe3ec;
  border-radius: 8px;
  padding: 0.7rem 0.8rem;
  resize: vertical;
  font-family: inherit;
  font-size: 0.92rem;
}

.chiamate-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.85rem 1rem 1rem;
}

.chiamate-btn-secondary,
.chiamate-btn-primary {
  border-radius: 8px;
  padding: 0.45rem 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid transparent;
}

.chiamate-btn-secondary {
  background: #f1f5f9;
  border-color: #e2e8f0;
  color: #334155;
}

.chiamate-btn-primary {
  background: #1d4ed8;
  color: #fff;
}

.chiamate-btn-primary:disabled,
.chiamate-btn-secondary:disabled {
  opacity: 0.65;
  cursor: default;
}

@media (min-width: 769px) {
  .info-button-container {
    justify-content: flex-end;
  }
}

:global(.home-page-mobile .layout-main-container) {
  padding: 0 !important;
  margin: 0 !important;
}

:global(.home-page-mobile .layout-main) {
  margin-top: 20px !important;
}

:global(.table-container .layout-main) {
  margin-top: 20px !important;
}

@media (max-width: 768px) {
  .grid.home-page-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    margin: 0;
    padding: 0 !important;
    width: 100vw;
    max-width: 100%;
  }

  .welcome-card {
    flex: 0;
    border-radius: 0 !important;
    margin: 0 !important;
    padding: 4rem 0 !important;
    min-height: auto;
    box-sizing: border-box;
    width: 100%;
    max-width: 100vw;
  }

  .welcome-card--compact {
    padding: 2rem 1rem !important;
  }

  .assistenza-panel--ticket,
  .assistenza-panel--chiamate {
    height: auto;
    min-height: 70vh;
    max-height: none;
  }

  .assistenza-panel-body--ticket {
    min-height: 60vh;
  }

  .assistenza-panel-body--chiamate {
    height: auto;
    max-height: none;
    min-height: 60vh;
  }

  .info-button-container {
    padding: 2rem 0;
    width: 100%;
  }
}

@media (max-width: 576px) {
  .welcome-title {
    font-size: 2.5rem;
  }

  .welcome-title--compact {
    font-size: 1.75rem;
  }

  .email {
    font-size: 1rem;
  }

  .welcome-card {
    padding: 3rem 0 !important;
  }
}

@media (max-width: 375px) {
  .welcome-title {
    font-size: 2rem;
  }

  .email {
    font-size: 0.9rem;
  }

  .welcome-card {
    padding: 2rem 0 !important;
  }
}

.wizard-banner {
  box-shadow: 0px 3px 5px rgba(0, 0, 0, 0.02), 0px 0px 2px rgba(0, 0, 0, 0.05), 0px 1px 4px rgba(0, 0, 0, 0.08) !important;
  background: linear-gradient(45deg, #0098f0 70%, #00f2db) !important;
  border: none;
  height: 100px;
  font-size: 40px;
  border-radius: 10px;
}
</style>
